/** Settings: company block, defaults, terms, AI proxy config, storage status. */
import { useEffect, useRef, useState } from 'preact/hooks';
import { putSettings } from '../../core/db';
import type { Settings } from '../../core/types';
import { refreshCore, settingsSig, toast } from '../state';
import { BASEMAPS } from '../../providers/basemap';
import { proxyUrlProblem, refreshUsage } from '../../ai/client';
import { BUILD } from '../../core/build';

export function SettingsView() {
  const s = settingsSig.value;
  const [draft, setDraft] = useState<Settings | null>(s);
  const [dirty, setDirty] = useState(false);
  const [storage, setStorage] = useState<{ usage: number; quota: number; persisted: boolean } | null>(null);
  /** the draft as of the last keystroke, not as of the last paint */
  const latest = useRef<Settings | null>(s);

  // Another tab changed the record, so take the new one — unless something has
  // been typed here and not saved yet. Unsaved edits are never thrown away
  // silently; the save merges over what is stored (db.ts putSettings).
  useEffect(() => {
    if (dirty) return;
    latest.current = settingsSig.value;
    setDraft(settingsSig.value);
  }, [settingsSig.value, dirty]);
  useEffect(() => {
    void (async () => {
      try {
        const est = await navigator.storage?.estimate?.();
        const persisted = await navigator.storage?.persisted?.();
        if (est) setStorage({ usage: est.usage ?? 0, quota: est.quota ?? 0, persisted: !!persisted });
      } catch { /* storage API unavailable — the panel simply doesn't render */ }
    })();
  }, []);

  if (!draft) return <div class="muted">Loading settings…</div>;
  const d = draft;
  /**
   * Every edit builds on the draft as it is now, not on the copy this render
   * closed over.
   *
   * Two fields changed before the screen re-drew both started from the same
   * copy, so the second wrote over the first and the first was silently gone.
   * A paste into two boxes does that, and so does a password manager filling a
   * form. The nested blocks needed their own updaters for the same reason:
   * `{ ...d.company, phone }` reaches back into the stale copy however careful
   * the outer merge is, and changing the markup and the tax in quick
   * succession would drop one of them, which is money.
   */
  const commit = (next: Settings) => { latest.current = next; setDirty(true); setDraft(next); };
  const now = () => latest.current ?? d;
  const up = (patch: Partial<Settings>) => commit({ ...now(), ...patch });
  const upCompany = (patch: Partial<Settings['company']>) => {
    const base = now();
    commit({ ...base, company: { ...base.company, ...patch } });
  };
  const upDefaults = (patch: Partial<Settings['defaults']>) => {
    const base = now();
    commit({ ...base, defaults: { ...base.defaults, ...patch } });
  };

  async function save() {
    // the newest edits, not the ones this render happens to have drawn
    const toSave = now();
    // Catch a mistyped proxy address here, where it was typed, rather than three
    // screens later as a confusing AI error (audit 2026-07-25).
    const problem = proxyUrlProblem(toSave.proxyUrl);
    if (problem) { toast(problem); return; }
    await putSettings(toSave);
    setDirty(false);
    await refreshCore();
    void refreshUsage(); // pick up the AI budget chip immediately when a proxy is configured
    toast('Settings saved');
  }

  return (
    <div style="max-width:760px;">
      <div class="pagehead">
        <div>
          <h1>Settings</h1>
          <p class="small muted mono" aria-label="App build">Build {BUILD}</p>
        </div>
        <button class="btn btn-primary" onClick={() => void save()}>Save settings</button>
      </div>

      <section class="card" style="margin-bottom:16px;">
        <h2 style="margin-bottom:12px;">Company — printed on every document</h2>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="field"><label>Company name</label>
            <input aria-label="Company name" value={d.company.name} onInput={(e) => upCompany({ name: (e.target as HTMLInputElement).value })} style="width:100%;" /></div>
          <div class="field"><label>Owner</label>
            <input aria-label="Owner" value={d.company.owner} onInput={(e) => upCompany({ owner: (e.target as HTMLInputElement).value })} style="width:100%;" /></div>
          <div class="field"><label>Phone</label>
            <input aria-label="Phone" value={d.company.phone} onInput={(e) => upCompany({ phone: (e.target as HTMLInputElement).value })} style="width:100%;" /></div>
          <div class="field"><label>Email</label>
            <input aria-label="Email" value={d.company.email} onInput={(e) => upCompany({ email: (e.target as HTMLInputElement).value })} style="width:100%;" /></div>
          <div class="field"><label>AZ ROC license #</label>
            <input aria-label="AZ ROC license number" value={d.company.roc} onInput={(e) => upCompany({ roc: (e.target as HTMLInputElement).value })} style="width:100%;" /></div>
        </div>
      </section>

      <section class="card" style="margin-bottom:16px;">
        <h2 style="margin-bottom:12px;">Bid defaults for new jobs</h2>
        <div style="display:flex;gap:12px;flex-wrap:wrap;">
          <Num label="Markup %" v={d.defaults.markupPct} on={(n) => upDefaults({ markupPct: n })} />
          <Num label="Tax %" v={d.defaults.taxPct} on={(n) => upDefaults({ taxPct: n })} />
          <Num label="Deposit %" v={d.defaults.depositPct} on={(n) => upDefaults({ depositPct: n })} />
          <Num label="Valid (days)" v={d.defaults.validityDays} on={(n) => upDefaults({ validityDays: n })} />
          <div class="field"><label>Default basemap</label>
            <select aria-label="Default basemap" value={d.basemap} onChange={(e) => up({ basemap: (e.target as HTMLSelectElement).value })}>
              {BASEMAPS.map((b) => (
                <option key={b.key} value={b.key}>{b.label}{b.note ? ` — ${b.note}` : ''}</option>
              ))}
            </select></div>
        </div>
      </section>

      <section class="card" style="margin-bottom:16px;">
        <h2 style="margin-bottom:12px;">Standard terms — printed on proposals</h2>
        <textarea aria-label="Standard terms" style="width:100%;min-height:220px;font-size:13.5px;" value={d.termsText}
          onInput={(e) => up({ termsText: (e.target as HTMLTextAreaElement).value })} />
        <p class="small muted" style="margin-top:6px;">Terms are frozen into each document when it is issued. Editing here only affects future documents. Template text is not legal advice — have an Arizona attorney review it once.</p>
      </section>

      <section class="card" style="margin-bottom:16px;">
        <h2 style="margin-bottom:12px;">AI assistant connection</h2>
        <div class="field"><label>Proxy address</label>
          <input aria-label="Proxy address" placeholder="https://…workers.dev — provided at handoff" value={d.proxyUrl}
            onInput={(e) => up({ proxyUrl: (e.target as HTMLInputElement).value })} style="width:100%;" /></div>
        <div class="field"><label>Device passphrase</label>
          <input aria-label="Device passphrase" type="password" placeholder="Entered once per device; never stored in the app's code" value={d.proxyPassphrase}
            onInput={(e) => up({ proxyPassphrase: (e.target as HTMLInputElement).value })} style="width:100%;" /></div>
        <p class="small muted">Leave both blank to run fully manual. Every AI feature has a manual path; nothing in the app requires this connection.</p>
      </section>

      {storage && (
        <section class="card">
          <h2 style="margin-bottom:8px;">Storage on this device</h2>
          <p class="small">
            Using {(storage.usage / 1048576).toFixed(1)} MB of {(storage.quota / 1073741824).toFixed(1)} GB available.
            Persistent storage: <b>{storage.persisted ? 'granted — the browser will not evict this data' : 'not granted — keep exports current, especially on iPhone'}</b>.
          </p>
        </section>
      )}
    </div>
  );
}

function Num({ label, v, on }: { label: string; v: number; on: (n: number) => void }) {
  return (
    <div class="field"><label>{label}</label>
      <input aria-label={label} type="number" inputMode="decimal" style="width:110px;" value={String(v)}
        onChange={(e) => { const n = parseFloat((e.target as HTMLInputElement).value); if (Number.isFinite(n)) on(n); }} />
    </div>
  );
}
